import { Controller } from "stimulus";
import consumer from "../channels/consumer";

const LIVE_STATUS = "live";
const PENDING_STATUS = "pending";
const IDLE_STATUS = "idle";
const ERROR_STATUS = "error";
const FINALIZATION_TIMEOUT_MS = 4000;
const FIRST_CHUNK_TIMEOUT_MS = 5000;
const DEEPL_PCM_SAMPLE_RATE = 16000;
const PCM_BUFFER_SIZE = 4096;
const PCM_NOISE_GATE_THRESHOLD = 0.012;
const AUTO_SILENCE_STOP_MS = 8000;
const AUTO_SPEECH_START_CHUNKS = 2;
const BASE_LANGUAGE = "fr";
const CAPTION_LINE_GROUP_WINDOW_MS = 2500;
const CAPTION_LINE_MAX_LENGTH = 140;
const MICROPHONE_STORAGE_KEY = "live-captions-selected-microphone-id";
const WEGLOT_TO_DEEPL_TARGET_LANGUAGE = {
  ar: "ar",
  de: "de",
  en: "en-US",
  "en-us": "en-US",
  "en-gb": "en-GB",
  es: "es",
  fr: "fr",
  it: "it",
  ja: "ja",
  ko: "ko",
  nl: "nl",
  pl: "pl",
  pt: "pt-PT",
  "pt-br": "pt-BR",
  "pt-pt": "pt-PT",
  tr: "tr",
  uk: "uk",
  zh: "zh-HANS",
  "zh-cn": "zh-HANS",
  "zh-hans": "zh-HANS"
};

export default class extends Controller {
  static targets = [
    "toggleButton",
    "settings",
    "sourceLanguageSelect",
    "microphoneSelect",
    "translationToggle",
    "languageSelect",
    "status",
    "error",
    "sourceOutput",
    "sourceEmpty",
    "debugOutput"
  ];

  static values = {
    sessionUrl: String,
    reconnectUrl: String,
    chunkMs: { type: Number, default: 250 },
    meetingId: Number,
    meetingToken: String,
    currentUserId: Number,
    speakerName: String,
    sourceLanguage: { type: String, default: "fr" },
    sourceLanguageMode: { type: String, default: "auto" }
  };

  connect() {
    // LOCAL TESTING ONLY: creates a browser-tab identity so two tabs using the same user account can exchange captions.
    // PROD VERSION: remove/comment this line and the buildClientId method at the bottom of this controller.
    this.clientId = this.buildClientId();
    this.peerTargetLanguage = null;
    this.peerTranslationEnabled = false;
    this.isRestartingForLocalPreferences = false;
    this.isRestartingForPeerPreferences = false;
    this.manualTargetLanguageSelected = false;
    this.userToggledStreaming = false;
    this.automaticStreaming = false;
    this.autoPausedForSilence = false;
    this.automaticSilenceStopInProgress = false;
    this.automaticStartInProgress = false;
    this.automaticStreamingEvaluationTimer = null;
    this.automaticStreamingEvaluationRunning = false;
    this.automaticStreamingEvaluationRequested = false;
    this.lastAutomaticBlockReason = "jitsi_not_joined";
    this.lastAutomaticStreamingError = "none";
    this.voiceMonitorRunning = false;
    this.voiceMonitorStarting = false;
    this.voiceMonitorVoiceStreak = 0;
    this.voiceMonitorLastRms = 0;
    this.voiceMonitorMaxRms = 0;
    this.voiceMonitorStream = null;
    this.voiceMonitorAudioContext = null;
    this.voiceMonitorSource = null;
    this.voiceMonitorProcessorNode = null;
    this.voiceMonitorSilentGainNode = null;
    this.jitsiState = this.defaultJitsiState();
    this.meetingChannelConnected = false;
    this.subscribeToMeetingChannel();
    this.boundHandleDeviceChange = this.handleDeviceChange.bind(this);
    this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
    this.boundHandleJitsiState = this.handleJitsiState.bind(this);
    this.boundHandleWeglotLanguageChange = this.handleWeglotLanguageChange.bind(this);
    this.boundHandleWeglotLanguageClick = this.handleWeglotLanguageClick.bind(this);
    navigator.mediaDevices?.addEventListener?.("devicechange", this.boundHandleDeviceChange);
    document.addEventListener("click", this.boundHandleDocumentClick);
    document.addEventListener("live-captions:jitsi-state", this.boundHandleJitsiState);
    document.addEventListener("click", this.boundHandleWeglotLanguageClick, true);
    this.subscribeToWeglotLanguageChanges();
    this.resetState();
    this.loadPreferredMicrophoneId();
    this.refreshMicrophoneOptions();
    this.syncTargetLanguageFromWeglot();
    this.syncTranslationControls();
    this.renderSourceTranscript();
    this.renderTranslationTranscript();
    this.renderDebug();
    this.waitForWeglotLanguage();
    this.queueAutomaticStreamingEvaluation();
  }

  disconnect() {
    this.unsubscribeFromMeetingChannel();
    navigator.mediaDevices?.removeEventListener?.("devicechange", this.boundHandleDeviceChange);
    document.removeEventListener("click", this.boundHandleDocumentClick);
    document.removeEventListener("live-captions:jitsi-state", this.boundHandleJitsiState);
    document.removeEventListener("click", this.boundHandleWeglotLanguageClick, true);
    this.unsubscribeFromWeglotLanguageChanges();
    this.stopWaitingForWeglotLanguage();
    this.clearAutomaticStreamingEvaluation();
    this.stopVoiceActivityMonitor();
    this.teardown();
  }

  toggleTranslation() {
    const shouldRestartStreaming = this.isRunning && this.targetLanguagesChanged();
    this.autoPausedForSilence = false;

    this.resetRemoteTranslationBuffer();
    this.syncTranslationControls();
    this.renderTranslationTranscript();
    this.broadcastTranslationPreferences();
    this.renderDebug();
    this.queueAutomaticStreamingEvaluation();

    if (shouldRestartStreaming) {
      this.restartStreamingForLocalPreferences();
    }
  }

  targetLanguageChanged() {
    this.manualTargetLanguageSelected = true;
    this.toggleTranslation();
  }

  microphoneChanged() {
    this.selectedMicrophoneId = this.hasMicrophoneSelectTarget ? this.microphoneSelectTarget.value : "";
    this.persistPreferredMicrophoneId();
    if (this.voiceMonitorRunning || this.voiceMonitorStarting) {
      this.stopVoiceActivityMonitor();
      this.queueAutomaticStreamingEvaluation();
    }
    this.renderDebug();
  }

  handleDeviceChange() {
    this.refreshMicrophoneOptions();
  }

  handleDocumentClick(event) {
    if (!this.hasSettingsTarget || !this.settingsTarget.open || this.settingsTarget.contains(event.target)) {
      return;
    }

    this.settingsTarget.open = false;
  }

  handleJitsiState(event) {
    this.jitsiState = {
      ...this.defaultJitsiState(),
      ...(event.detail || {}),
      remoteAudioMuted: { ...(event.detail?.remoteAudioMuted || {}) }
    };
    this.queueAutomaticStreamingEvaluation();
    this.renderDebug();
  }

  defaultJitsiState() {
    return {
      conferenceJoined: false,
      participantCount: 0,
      localParticipantId: null,
      localAudioMuted: true,
      localAudioAvailable: true,
      remoteAudioMuted: {},
      reason: "initial"
    };
  }

  handleWeglotLanguageChange(language) {
    this.syncTargetLanguageFromWeglot(language);
  }

  handleWeglotLanguageClick(event) {
    const languageElement = event.target?.closest?.("[id^='weglot-language-']");
    const language = this.languageFromWeglotElement(languageElement);
    if (!language) {
      return;
    }

    window.setTimeout(() => {
      this.syncTargetLanguageFromWeglot(language);
    }, 0);
  }

  subscribeToWeglotLanguageChanges() {
    if (!window.Weglot || typeof window.Weglot.on !== "function") {
      return;
    }

    try {
      window.Weglot.on("languageChanged", this.boundHandleWeglotLanguageChange);
      this.weglotLanguageChangeSubscribed = true;
    } catch (_error) {
      this.weglotLanguageChangeSubscribed = false;
    }
  }

  waitForWeglotLanguage() {
    this.stopWaitingForWeglotLanguage();
    if (this.weglotLanguageChangeSubscribed || document.body?.dataset?.weglotEnabled !== "true") {
      return;
    }

    this.weglotWaitAttempts = 0;
    this.weglotWaitInterval = window.setInterval(() => {
      this.weglotWaitAttempts += 1;
      this.subscribeToWeglotLanguageChanges();
      this.syncTargetLanguageFromWeglot();

      if (this.weglotLanguageChangeSubscribed || this.weglotWaitAttempts >= 20) {
        this.stopWaitingForWeglotLanguage();
      }
    }, 250);
  }

  stopWaitingForWeglotLanguage() {
    if (this.weglotWaitInterval) {
      window.clearInterval(this.weglotWaitInterval);
      this.weglotWaitInterval = null;
    }
  }

  unsubscribeFromWeglotLanguageChanges() {
    if (!this.weglotLanguageChangeSubscribed || !window.Weglot || typeof window.Weglot.off !== "function") {
      return;
    }

    try {
      window.Weglot.off("languageChanged", this.boundHandleWeglotLanguageChange);
    } catch (_error) {
      // Ignore Weglot teardown failures.
    }
    this.weglotLanguageChangeSubscribed = false;
  }

  syncTargetLanguageFromWeglot(language = null) {
    if (this.manualTargetLanguageSelected) {
      return;
    }

    const weglotLanguage = language || this.currentWeglotLanguage();
    const deeplLanguage = this.deeplTargetLanguageForWeglotLanguage(weglotLanguage);
    if (!deeplLanguage || !this.hasLanguageSelectTarget || !this.languageSelectHasValue(deeplLanguage)) {
      return;
    }

    const shouldRestartStreaming = this.isRunning && this.languageSelectTarget.value !== deeplLanguage;
    this.languageSelectTarget.value = deeplLanguage;
    this.lastWeglotLanguage = weglotLanguage;
    this.syncTranslationControls();
    this.broadcastTranslationPreferences();
    this.renderDebug();
    this.autoPausedForSilence = false;
    this.queueAutomaticStreamingEvaluation();

    if (shouldRestartStreaming) {
      this.restartStreamingForLocalPreferences();
    }
  }

  currentWeglotLanguage() {
    if (window.Weglot && typeof window.Weglot.getCurrentLang === "function") {
      try {
        return window.Weglot.getCurrentLang();
      } catch (_error) {
        return this.currentWeglotLanguageFromDom();
      }
    }

    return this.currentWeglotLanguageFromDom();
  }

  currentWeglotLanguageFromDom() {
    const selectedLanguageElement = document.querySelector(
      "[id^='weglot-language-'][aria-current='true'], [id^='weglot-language-'].active, [id^='weglot-language-'].wgcurrent, [id^='weglot-language-'].weglot-active"
    );

    return this.languageFromWeglotElement(selectedLanguageElement);
  }

  languageFromWeglotElement(element) {
    if (!element?.id) {
      return null;
    }

    return element.id.replace(/^weglot-language-/, "") || null;
  }

  deeplTargetLanguageForWeglotLanguage(language) {
    const normalizedLanguage = this.normalizeLanguageTag(language);
    if (!normalizedLanguage) {
      return null;
    }

    return WEGLOT_TO_DEEPL_TARGET_LANGUAGE[normalizedLanguage] || WEGLOT_TO_DEEPL_TARGET_LANGUAGE[normalizedLanguage.split("-")[0]] || null;
  }

  languageSelectHasValue(value) {
    return Array.from(this.languageSelectTarget.options).some((option) => option.value === value);
  }

  queueAutomaticStreamingEvaluation() {
    if (this.automaticStreamingEvaluationTimer) {
      return;
    }

    this.automaticStreamingEvaluationTimer = window.setTimeout(() => {
      this.automaticStreamingEvaluationTimer = null;
      this.applyAutomaticStreamingState();
    }, 0);
  }

  clearAutomaticStreamingEvaluation() {
    if (this.automaticStreamingEvaluationTimer) {
      window.clearTimeout(this.automaticStreamingEvaluationTimer);
      this.automaticStreamingEvaluationTimer = null;
    }
  }

  async applyAutomaticStreamingState() {
    if (this.automaticStreamingEvaluationRunning) {
      this.automaticStreamingEvaluationRequested = true;
      return;
    }

    this.automaticStreamingEvaluationRunning = true;

    try {
      const blockReason = this.automaticStreamingBlockReason();
      this.lastAutomaticBlockReason = blockReason || "none";

      if (blockReason) {
        this.autoPausedForSilence = false;
        this.stopVoiceActivityMonitor();

        if (this.isRunning && this.automaticStreaming) {
          await this.stopStreaming({
            preserveTranscript: true,
            finalStatusMessage: this.automaticStatusMessage(blockReason),
            finalStatus: IDLE_STATUS
          });
        } else if (!this.isRunning && !this.userToggledStreaming) {
          this.setStatus(this.automaticStatusMessage(blockReason), IDLE_STATUS);
        }

        this.renderDebug();
        return;
      }

      if (this.isRunning || this.automaticStartInProgress) {
        return;
      }

      if (this.autoPausedForSilence) {
        this.startVoiceActivityMonitor();
        return;
      }

      await this.startAutomaticStreaming();
    } catch (error) {
      this.lastAutomaticStreamingError = error.message || "automatic_streaming_error";
      this.renderDebug();
    } finally {
      this.automaticStreamingEvaluationRunning = false;
      if (this.automaticStreamingEvaluationRequested) {
        this.automaticStreamingEvaluationRequested = false;
        this.queueAutomaticStreamingEvaluation();
      }
    }
  }

  automaticStreamingBlockReason() {
    if (this.userToggledStreaming) {
      return "manual_override";
    }

    if (!this.automaticTranslationNeeded()) {
      return "no_non_french_translation_requested";
    }

    if (!this.jitsiState?.conferenceJoined) {
      return "jitsi_not_joined";
    }

    if ((this.jitsiState.participantCount || 0) < 2) {
      return "waiting_for_second_participant";
    }

    if (this.jitsiState.localAudioAvailable === false) {
      return "local_audio_unavailable";
    }

    if (this.jitsiState.localAudioMuted) {
      return "local_microphone_muted";
    }

    if (this.remoteAudioMuted()) {
      return "remote_microphone_muted";
    }

    return null;
  }

  automaticTranslationNeeded() {
    return this.outgoingTargetLanguages.some((language) => this.normalizeLanguageTag(language) !== BASE_LANGUAGE);
  }

  remoteAudioMuted() {
    return Object.values(this.jitsiState?.remoteAudioMuted || {}).some((isMuted) => Boolean(isMuted));
  }

  automaticStatusMessage(reason) {
    const messages = {
      manual_override: "Sous-titres en mode manuel.",
      no_non_french_translation_requested: "Sous-titres inactifs.",
      jitsi_not_joined: "En attente de la visioconference.",
      waiting_for_second_participant: "En attente de l'interlocuteur.",
      local_audio_unavailable: "Micro indisponible dans Jitsi.",
      local_microphone_muted: "Micro coupe.",
      remote_microphone_muted: "Micro interlocuteur coupe."
    };

    return messages[reason] || "Sous-titres inactifs.";
  }

  async startAutomaticStreaming() {
    if (this.isRunning || this.automaticStartInProgress || this.automaticStreamingBlockReason()) {
      return;
    }

    this.automaticStartInProgress = true;
    this.stopVoiceActivityMonitor();

    try {
      await this.startStreaming({ preserveTranscript: true, automatic: true });
    } finally {
      this.automaticStartInProgress = false;
    }
  }

  async toggleStreaming(event) {
    event?.preventDefault();
    this.userToggledStreaming = true;
    this.autoPausedForSilence = false;
    this.automaticStreaming = false;
    this.stopVoiceActivityMonitor();
    if (this.isRunning) {
      await this.stopStreaming();
    } else {
      await this.startStreaming({ automatic: false });
    }
  }

  async startStreaming({ preserveTranscript = false, automatic = false } = {}) {
    if (this.isRunning) {
      return;
    }

    if (!this.browserIsSupported()) {
      this.setError("Ce navigateur ne supporte pas la capture micro requise. Pour ce test, utilisez Chrome ou Edge recents.");
      return;
    }

    if (!this.audioContextClass()) {
      this.setError("Ce navigateur ne supporte pas la capture audio Web requise. Pour ce test, utilisez Chrome ou Edge.");
      return;
    }

    this.clearMessages();
    if (!preserveTranscript) {
      this.resetTranscriptBuffers();
    }
    this.syncTranslationControls();
    this.renderSourceTranscript();
    this.renderTranslationTranscript();
    this.setStatus("Autorisation du microphone...", PENDING_STATUS);
    this.automaticStreaming = automatic;
    this.streamingStartedAt = null;
    this.lastVoiceDetectedAt = null;

    try {
      this.selectedMicrophoneId = this.hasMicrophoneSelectTarget ? this.microphoneSelectTarget.value : this.selectedMicrophoneId;
      this.mediaStream = await this.requestMicrophoneStream();

      this.captureTrack = this.mediaStream.getAudioTracks()[0] || null;
      this.captureTrackSettings = this.captureTrack?.getSettings?.() || {};
      if (this.captureTrackSettings.deviceId) {
        this.selectedMicrophoneId = this.captureTrackSettings.deviceId;
        this.persistPreferredMicrophoneId();
      }
      await this.refreshMicrophoneOptions();

      await this.setupAudioPipeline();
      const requestedTargetLanguages = this.outgoingTargetLanguages;
      const session = await this.postJson(this.sessionUrlValue, this.buildSessionPayload(requestedTargetLanguages));
      this.currentToken = session.token;
      this.streamingUrl = session.streaming_url;
      this.sessionId = session.session_id;
      this.activeTargetLanguages = requestedTargetLanguages;
      this.intentionalStop = false;
      this.endOfSourceSent = false;
      this.reconnectAttempts = 0;
      this.receivedTerminalEvent = false;
      this.pendingChunks = [];
      this.streamingStartedAt = Date.now();
      this.lastVoiceDetectedAt = null;

      this.setStatus("Connexion a DeepL...", PENDING_STATUS);
      await this.openSocket(session.streaming_url, session.token);
      this.lastSocketEvent = "socket_open";
      this.isRunning = true;
      this.toggleControls(true);
      this.startRecorder();
      this.setStatus("Sous-titres en direct actifs.", LIVE_STATUS);
      this.renderDebug();

      if (this.targetLanguagesChanged()) {
        this.restartStreamingForLocalPreferences();
      }
    } catch (error) {
      this.setError(error.message || "Impossible de lancer les sous-titres.");
      this.teardown();
    }
  }

  normalizeLanguageTag(language) {
    return (language || "").toLowerCase();
  }

  buildSessionPayload(targetLanguages = this.outgoingTargetLanguages) {
    const payload = {
      source_language: this.currentSourceLanguage,
      source_language_mode: this.currentSourceLanguageMode,
      source_media_content_type: this.sourceMediaContentType,
      target_languages: targetLanguages
    };

    return payload;
  }

  browserIsSupported() {
    return Boolean(
      navigator.mediaDevices?.getUserMedia &&
      navigator.mediaDevices?.enumerateDevices &&
      window.WebSocket &&
      this.audioContextClass()
    );
  }

  audioContextClass() {
    return window.AudioContext || window.webkitAudioContext;
  }

  get translationEnabled() {
    return this.translationToggleTarget.checked;
  }

  get outgoingTargetLanguages() {
    var languages = [];

    if (this.peerTranslationEnabled && this.peerTargetLanguage) {
      languages.push(this.peerTargetLanguage);
    }

    if (this.translationEnabled) {
      var localLang = this.languageSelectTarget.value;
      if (localLang && languages.indexOf(localLang) === -1) {
        languages.push(localLang);
      }
    }

    return languages;
  }

  get selectedSourceLanguageValue() {
    if (this.hasSourceLanguageSelectTarget) {
      return this.sourceLanguageSelectTarget.value;
    }

    return this.sourceLanguageModeValue === "auto" ? "auto" : this.sourceLanguageValue;
  }

  get currentSourceLanguageMode() {
    return this.selectedSourceLanguageValue === "auto" ? "auto" : "fixed";
  }

  get currentSourceLanguage() {
    return this.currentSourceLanguageMode === "fixed" ? this.selectedSourceLanguageValue : null;
  }

  syncTranslationControls() {
    this.languageSelectTarget.disabled = !this.translationEnabled;
  }

  toggleControls(running) {
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.dataset.running = running ? "true" : "false";
      this.toggleButtonTarget.textContent = running ? "Arreter" : "Lancer";
    }
    this.sourceLanguageSelectTarget.disabled = running;
    if (this.hasMicrophoneSelectTarget) {
      this.microphoneSelectTarget.disabled = running;
    }
    this.translationToggleTarget.disabled = false;
    this.languageSelectTarget.disabled = !this.translationEnabled;
  }

  loadPreferredMicrophoneId() {
    try {
      this.selectedMicrophoneId = window.localStorage.getItem(MICROPHONE_STORAGE_KEY) || "";
    } catch (_error) {
      this.selectedMicrophoneId = "";
    }
  }

  persistPreferredMicrophoneId() {
    try {
      if (this.selectedMicrophoneId) {
        window.localStorage.setItem(MICROPHONE_STORAGE_KEY, this.selectedMicrophoneId);
      } else {
        window.localStorage.removeItem(MICROPHONE_STORAGE_KEY);
      }
    } catch (_error) {
      // Ignore storage failures.
    }
  }

  async refreshMicrophoneOptions() {
    if (!this.hasMicrophoneSelectTarget || !navigator.mediaDevices?.enumerateDevices) {
      return;
    }

    const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
    const microphones = devices.filter((device) => device.kind === "audioinput");
    const fallbackOptionLabel = microphones.length === 0 ? "Aucun microphone détecté" : "Microphone par défaut";
    const currentValue = this.selectedMicrophoneId || this.microphoneSelectTarget.value || "";

    this.microphoneSelectTarget.innerHTML = "";
    this.microphoneSelectTarget.append(this.buildOption("", fallbackOptionLabel));

    microphones.forEach((device, index) => {
      const label = device.label || `Microphone ${index + 1}`;
      this.microphoneSelectTarget.append(this.buildOption(device.deviceId, label));
    });

    const hasCurrentValue = microphones.some((device) => device.deviceId === currentValue);
    this.microphoneSelectTarget.value = hasCurrentValue ? currentValue : "";
    this.selectedMicrophoneId = this.microphoneSelectTarget.value;
    this.persistPreferredMicrophoneId();
    this.renderDebug();
  }

  buildAudioConstraints(deviceId) {
    return {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      ...(deviceId ? { deviceId: { exact: deviceId } } : {})
    };
  }

  async requestMicrophoneStream() {
    const preferredDeviceId = this.selectedMicrophoneId || "";

    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: this.buildAudioConstraints(preferredDeviceId)
      });
    } catch (error) {
      const missingPreferredDevice = preferredDeviceId && ["NotFoundError", "OverconstrainedError"].includes(error?.name);
      if (!missingPreferredDevice) {
        throw error;
      }

      this.selectedMicrophoneId = "";
      this.persistPreferredMicrophoneId();
      if (this.hasMicrophoneSelectTarget) {
        this.microphoneSelectTarget.value = "";
      }

      return navigator.mediaDevices.getUserMedia({
        audio: this.buildAudioConstraints("")
      });
    }
  }

  buildOption(value, label) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = label;
    return option;
  }

  clearMessages() {
    this.errorTarget.textContent = "";
    this.errorTarget.hidden = true;
  }

  setStatus(message, status) {
    this.statusTarget.textContent = message;
    this.statusTarget.dataset.state = status;
  }

  setError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
    this.setStatus("Sous-titres inactifs.", ERROR_STATUS);
  }

  resetState() {
    this.isRunning = false;
    this.intentionalStop = false;
    this.endOfSourceSent = false;
    this.receivedTerminalEvent = false;
    this.reconnectAttempts = 0;
    this.pendingChunks = [];
    this.activeTargetLanguages = [];
    this.mediaStream = null;
    this.selectedMicrophoneId = this.selectedMicrophoneId || "";
    this.captureTrack = null;
    this.captureTrackSettings = {};
    this.audioContext = null;
    this.mediaStreamSource = null;
    this.processorNode = null;
    this.silentGainNode = null;
    this.socket = null;
    this.firstChunkTimeout = null;
    this.currentToken = null;
    this.streamingUrl = null;
    this.sessionId = null;
    this.finalizationResolver = null;
    this.streamingStartedAt = null;
    this.lastVoiceDetectedAt = null;
    this.chunksProducedCount = 0;
    this.chunksSentCount = 0;
    this.sourceMediaContentType = null;
    this.recorderMimeType = null;
    this.audioSampleRate = null;
    this.targetAudioSampleRate = null;
    this.pcmSamplesPerChunk = 0;
    this.pcmSampleBuffer = [];
    this.lastChunkByteSize = 0;
    this.lastChunkRms = 0;
    this.maxChunkRms = 0;
    this.lastRawChunkRms = 0;
    this.maxRawChunkRms = 0;
    this.chunksWithSignalCount = 0;
    this.silenceChunkStreak = 0;
    this.lastSocketEvent = "none";
    this.lastSocketError = "none";
    this.transcriptMessageCount = 0;
    this.resetTranscriptBuffers();
    this.toggleControls(false);
    this.setStatus("Sous-titres inactifs.", IDLE_STATUS);
  }

  resetTranscriptBuffers() {
    this.nextTranscriptLineId = 1;
    // PROD VERSION:
    // this.sourceTranscript = { recentLines: [], tentativeText: "", tentativeSpeakerId: null, tentativeSpeakerName: "" };
    // this.translationTranscript = { recentLines: [], tentativeText: "", tentativeSpeakerId: null, tentativeSpeakerName: "", language: null };
    // LOCAL TESTING ONLY: these expanded objects add tentativeSpeakerClientId for same-account local testing.
    this.sourceTranscript = {
      recentLines: [],
      tentativeText: "",
      // LOCAL TESTING ONLY: keeps tentative text tied to the browser tab during same-account local testing.
      // PROD VERSION: remove/comment this tentativeSpeakerClientId line.
      tentativeSpeakerClientId: null,
      tentativeSpeakerId: null,
      tentativeSpeakerName: ""
    };
    this.translationTranscript = {
      recentLines: [],
      tentativeText: "",
      // LOCAL TESTING ONLY: keeps tentative translated text tied to the browser tab during same-account local testing.
      // PROD VERSION: remove/comment this tentativeSpeakerClientId line.
      tentativeSpeakerClientId: null,
      tentativeSpeakerId: null,
      tentativeSpeakerName: "",
      language: null
    };
  }

  async stopStreaming({ preserveTranscript = true, finalStatusMessage = "Sous-titres arretes.", finalStatus = IDLE_STATUS } = {}) {
    if (!this.isRunning && !this.socket && !this.mediaStream) {
      return;
    }

    this.intentionalStop = true;
    this.setStatus("Arret des sous-titres...", PENDING_STATUS);

    try {
      await this.stopRecorder();
      await this.sendEndOfSourceMedia();
      await this.waitForFinalization();
    } catch (error) {
      this.setError(error.message || "Erreur pendant l'arret des sous-titres.");
    } finally {
      this.teardown({ preserveTranscript });
      if (finalStatusMessage) {
        this.setStatus(finalStatusMessage, finalStatus);
      }
    }
  }

  teardown({ preserveTranscript = true } = {}) {
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.close();
      }
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
    }

    if (this.processorNode) {
      this.processorNode.onaudioprocess = null;
      this.processorNode.disconnect();
    }

    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
    }

    if (this.silentGainNode) {
      this.silentGainNode.disconnect();
    }

    if (this.audioContext && this.audioContext.state !== "closed") {
      this.audioContext.close().catch(() => {});
    }

    this.mediaStream = null;
    this.captureTrack = null;
    this.captureTrackSettings = {};
    this.audioContext = null;
    this.mediaStreamSource = null;
    this.processorNode = null;
    this.silentGainNode = null;
    this.socket = null;
    this.pendingChunks = [];
    this.isRunning = false;
    this.automaticStreaming = false;
    this.intentionalStop = false;
    this.endOfSourceSent = false;
    this.receivedTerminalEvent = false;
    this.reconnectAttempts = 0;
    this.clearFirstChunkWatchdog();
    this.currentToken = null;
    this.streamingUrl = null;
    this.finalizationResolver = null;
    this.renderDebug();
    this.toggleControls(false);
    this.syncTranslationControls();

    if (!preserveTranscript) {
      this.resetTranscriptBuffers();
      this.renderSourceTranscript();
      this.renderTranslationTranscript();
    }
  }

  async startVoiceActivityMonitor() {
    if (this.voiceMonitorRunning || this.voiceMonitorStarting || this.isRunning || this.automaticStreamingBlockReason()) {
      return;
    }

    const AudioContextClass = this.audioContextClass();
    if (!AudioContextClass || !navigator.mediaDevices?.getUserMedia) {
      return;
    }

    this.voiceMonitorStarting = true;
    this.voiceMonitorVoiceStreak = 0;
    this.voiceMonitorLastRms = 0;
    this.voiceMonitorMaxRms = 0;
    this.setStatus("En attente de parole...", PENDING_STATUS);

    try {
      this.voiceMonitorStream = await this.requestMicrophoneStream();

      if (this.isRunning || this.automaticStreamingBlockReason()) {
        this.stopVoiceActivityMonitor();
        return;
      }

      this.voiceMonitorAudioContext = new AudioContextClass({ latencyHint: "interactive" });
      if (this.voiceMonitorAudioContext.state === "suspended") {
        await this.voiceMonitorAudioContext.resume();
      }

      this.voiceMonitorSource = this.voiceMonitorAudioContext.createMediaStreamSource(this.voiceMonitorStream);
      this.voiceMonitorProcessorNode = this.voiceMonitorAudioContext.createScriptProcessor(PCM_BUFFER_SIZE, 1, 1);
      this.voiceMonitorSilentGainNode = this.voiceMonitorAudioContext.createGain();
      this.voiceMonitorSilentGainNode.gain.value = 0;

      this.voiceMonitorProcessorNode.onaudioprocess = (event) => {
        this.handleVoiceMonitorAudioProcess(event);
      };

      this.voiceMonitorSource.connect(this.voiceMonitorProcessorNode);
      this.voiceMonitorProcessorNode.connect(this.voiceMonitorSilentGainNode);
      this.voiceMonitorSilentGainNode.connect(this.voiceMonitorAudioContext.destination);
      this.voiceMonitorRunning = true;
      this.renderDebug();
    } catch (error) {
      this.lastAutomaticStreamingError = error.message || "voice_monitor_error";
      this.renderDebug();
    } finally {
      this.voiceMonitorStarting = false;
    }
  }

  stopVoiceActivityMonitor() {
    if (this.voiceMonitorProcessorNode) {
      this.voiceMonitorProcessorNode.onaudioprocess = null;
      this.voiceMonitorProcessorNode.disconnect();
    }

    if (this.voiceMonitorSource) {
      this.voiceMonitorSource.disconnect();
    }

    if (this.voiceMonitorSilentGainNode) {
      this.voiceMonitorSilentGainNode.disconnect();
    }

    if (this.voiceMonitorAudioContext && this.voiceMonitorAudioContext.state !== "closed") {
      this.voiceMonitorAudioContext.close().catch(() => {});
    }

    if (this.voiceMonitorStream) {
      this.voiceMonitorStream.getTracks().forEach((track) => track.stop());
    }

    this.voiceMonitorStream = null;
    this.voiceMonitorAudioContext = null;
    this.voiceMonitorSource = null;
    this.voiceMonitorProcessorNode = null;
    this.voiceMonitorSilentGainNode = null;
    this.voiceMonitorRunning = false;
    this.voiceMonitorStarting = false;
    this.voiceMonitorVoiceStreak = 0;
  }

  handleVoiceMonitorAudioProcess(event) {
    if (this.isRunning || this.automaticStreamingBlockReason()) {
      this.stopVoiceActivityMonitor();
      return;
    }

    const channelData = event.inputBuffer.getChannelData(0);
    if (!channelData || channelData.length === 0) {
      return;
    }

    const rms = this.calculateFloatRms(channelData);
    this.voiceMonitorLastRms = rms;
    this.voiceMonitorMaxRms = Math.max(this.voiceMonitorMaxRms, rms);
    this.voiceMonitorVoiceStreak = rms >= PCM_NOISE_GATE_THRESHOLD ? this.voiceMonitorVoiceStreak + 1 : 0;

    if (this.voiceMonitorVoiceStreak < AUTO_SPEECH_START_CHUNKS) {
      return;
    }

    this.autoPausedForSilence = false;
    this.stopVoiceActivityMonitor();
    this.startAutomaticStreaming();
  }

  async setupAudioPipeline() {
    const AudioContextClass = this.audioContextClass();
    this.audioContext = new AudioContextClass({ latencyHint: "interactive" });
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }

    this.audioSampleRate = this.audioContext.sampleRate;
    this.targetAudioSampleRate = DEEPL_PCM_SAMPLE_RATE;
    this.pcmSamplesPerChunk = Math.max(1, Math.round((this.targetAudioSampleRate * this.chunkMsValue) / 1000));
    this.pcmSampleBuffer = [];
    this.sourceMediaContentType = `audio/pcm;encoding=s16le;rate=${this.targetAudioSampleRate}`;
    this.recorderMimeType = this.sourceMediaContentType;

    this.mediaStreamSource = this.audioContext.createMediaStreamSource(this.mediaStream);
    this.processorNode = this.audioContext.createScriptProcessor(PCM_BUFFER_SIZE, 1, 1);
    this.silentGainNode = this.audioContext.createGain();
    this.silentGainNode.gain.value = 0;

    this.processorNode.onaudioprocess = (event) => {
      try {
        this.handleAudioProcess(event);
      } catch (_error) {
        this.setError("Impossible de traiter l'audio capture.");
        this.stopStreaming();
      }
    };

    this.mediaStreamSource.connect(this.processorNode);
    this.processorNode.connect(this.silentGainNode);
    this.silentGainNode.connect(this.audioContext.destination);
    this.renderDebug();
  }

  startRecorder() {
    this.startFirstChunkWatchdog();
  }

  async stopRecorder() {
    this.flushPcmSampleBuffer();

    if (this.processorNode) {
      this.processorNode.onaudioprocess = null;
      this.processorNode.disconnect();
      this.processorNode = null;
    }

    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }

    if (this.silentGainNode) {
      this.silentGainNode.disconnect();
      this.silentGainNode = null;
    }

    if (this.audioContext && this.audioContext.state !== "closed") {
      await this.audioContext.close().catch(() => {});
    }
    this.audioContext = null;
  }

  handleAudioProcess(event) {
    if (!this.isRunning) {
      return;
    }

    const channelData = event.inputBuffer.getChannelData(0);
    if (!channelData || channelData.length === 0) {
      return;
    }

    const rawChunkRms = this.calculateFloatRms(channelData);
    this.lastRawChunkRms = rawChunkRms;
    this.maxRawChunkRms = Math.max(this.maxRawChunkRms, rawChunkRms);

    const resampledChannelData = this.resampleChannelData(channelData, this.audioSampleRate, this.targetAudioSampleRate);
    for (let index = 0; index < resampledChannelData.length; index += 1) {
      this.pcmSampleBuffer.push(this.floatTo16BitSample(resampledChannelData[index]));
    }

    while (this.pcmSampleBuffer.length >= this.pcmSamplesPerChunk) {
      const chunk = this.pcmSampleBuffer.splice(0, this.pcmSamplesPerChunk);
      this.publishPcmChunk(chunk);
    }
  }

  flushPcmSampleBuffer() {
    if (!this.pcmSampleBuffer.length) {
      return;
    }

    const chunk = this.pcmSampleBuffer.splice(0, this.pcmSampleBuffer.length);
    this.publishPcmChunk(chunk);
  }

  publishPcmChunk(samples) {
    if (!samples.length) {
      return;
    }

    const chunkRms = this.calculateInt16Rms(samples);
    const voiceDetected = chunkRms >= PCM_NOISE_GATE_THRESHOLD;
    this.lastChunkRms = chunkRms;
    this.maxChunkRms = Math.max(this.maxChunkRms, chunkRms);

    if (voiceDetected) {
      this.lastVoiceDetectedAt = Date.now();
      this.chunksWithSignalCount += 1;
      this.silenceChunkStreak = 0;
      this.sendPcmChunk(samples);
    } else {
      this.silenceChunkStreak += 1;
      this.sendPcmChunk(this.buildSilentChunk(samples.length));
    }

    this.chunksProducedCount += 1;
    this.clearFirstChunkWatchdog();
    this.lastChunkByteSize = samples.length * 2;

    if (this.chunksProducedCount % 5 === 0) {
      this.renderDebug();
    }

    if (this.chunksProducedCount === 1) {
      this.setStatus("Micro detecte, transcription en cours...", LIVE_STATUS);
      this.renderDebug();
    }

    this.stopAutomaticStreamingForSilenceIfNeeded();
  }

  stopAutomaticStreamingForSilenceIfNeeded() {
    if (!this.automaticStreaming || this.automaticSilenceStopInProgress || !this.isRunning) {
      return;
    }

    const silenceReferenceTime = this.lastVoiceDetectedAt || this.streamingStartedAt;
    if (!silenceReferenceTime || Date.now() - silenceReferenceTime < AUTO_SILENCE_STOP_MS) {
      return;
    }

    this.stopAutomaticStreamingForSilence();
  }

  stopAutomaticStreamingForSilence() {
    if (this.automaticSilenceStopInProgress) {
      return;
    }

    this.automaticSilenceStopInProgress = true;
    this.autoPausedForSilence = true;
    this.stopStreaming({
      preserveTranscript: true,
      finalStatusMessage: "En attente de parole...",
      finalStatus: PENDING_STATUS
    }).catch((error) => {
      this.lastAutomaticStreamingError = error.message || "automatic_silence_stop_error";
      this.renderDebug();
    }).finally(() => {
      this.automaticSilenceStopInProgress = false;
      this.queueAutomaticStreamingEvaluation();
    });
  }

  sendPcmChunk(samples) {
    const base64Chunk = this.int16SamplesToBase64(samples);
    this.enqueueOrSendChunk(base64Chunk);
  }

  buildSilentChunk(length) {
    return new Int16Array(length);
  }

  enqueueOrSendChunk(base64Data) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ source_media_chunk: { data: base64Data } }));
      this.chunksSentCount += 1;
      if (this.chunksSentCount % 10 === 0) {
        this.renderDebug();
      }
      return;
    }

    this.pendingChunks.push(base64Data);
    if (this.pendingChunks.length > 20) {
      this.pendingChunks.shift();
    }
  }

  flushPendingChunks() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }

    this.pendingChunks.forEach((chunk) => {
      this.socket.send(JSON.stringify({ source_media_chunk: { data: chunk } }));
      this.chunksSentCount += 1;
    });
    this.pendingChunks = [];
  }

  async sendEndOfSourceMedia() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN || this.endOfSourceSent || this.receivedTerminalEvent) {
      return;
    }

    this.socket.send(JSON.stringify({ end_of_source_media: {} }));
    this.endOfSourceSent = true;
  }

  waitForFinalization() {
    if (this.receivedTerminalEvent || !this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.finalizationResolver = () => {
        resolve();
        this.finalizationResolver = null;
      };
      window.setTimeout(() => {
        resolve();
        this.finalizationResolver = null;
      }, FINALIZATION_TIMEOUT_MS);
    });
  }

  openSocket(streamingUrl, token) {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(`${streamingUrl}?token=${encodeURIComponent(token)}`);
      let resolved = false;

      socket.onopen = () => {
        resolved = true;
        this.socket = socket;
        this.flushPendingChunks();
        resolve();
      };

      socket.onmessage = async (event) => {
        this.lastSocketEvent = "message";
        try {
          await this.handleSocketMessage(event);
        } catch (error) {
          this.lastSocketError = error.message || "Message DeepL illisible.";
          this.renderDebug();
        }
      };

      socket.onerror = () => {
        this.lastSocketError = "socket_error";
        this.renderDebug();
        if (!resolved) {
          reject(new Error("Impossible d'ouvrir la connexion DeepL."));
        }
      };

      socket.onclose = (event) => {
        this.lastSocketEvent = `close:${event.code}`;
        this.lastSocketError = event.reason || this.lastSocketError;
        this.renderDebug();
        if (!resolved) {
          reject(new Error("La connexion DeepL s'est fermee avant d'etre prete."));
          return;
        }

        if (!this.intentionalStop && !this.receivedTerminalEvent) {
          this.handleUnexpectedClose(event);
        }
      };
    });
  }

  async handleUnexpectedClose(event) {
    if (this.reconnectAttempts >= 1 || !this.currentToken) {
      const closeDetail = event?.reason ? ` (${event.reason})` : "";
      this.setError(`Connexion DeepL interrompue. Relancez les sous-titres.${closeDetail}`);
      this.teardown({ preserveTranscript: true });
      return;
    }

    this.reconnectAttempts += 1;
    this.setStatus("Connexion interrompue, tentative de reconnexion...", PENDING_STATUS);

    try {
      const session = await this.postJson(this.reconnectUrlValue, { token: this.currentToken });
      this.currentToken = session.token;
      this.streamingUrl = session.streaming_url;
      await this.openSocket(session.streaming_url, session.token);
      this.setStatus("Connexion DeepL retablie.", LIVE_STATUS);
    } catch (error) {
      this.setError(error.message || "Echec de reconnexion a DeepL.");
      this.teardown({ preserveTranscript: true });
    }
  }

  async handleSocketMessage(event) {
    const payload = await this.decodeSocketPayload(event.data);
    const data = JSON.parse(payload);
    this.lastSocketEvent = Object.keys(data)[0] || "unknown";
    this.renderDebug();

    if (data.source_transcript_update) {
      this.transcriptMessageCount += 1;
      // LOCAL TESTING ONLY: speakerClientId lets same-user local tabs be treated as separate speakers.
      // PROD VERSION:
      // const localMeta = { speakerId: this.currentUserIdValue, speakerName: this.speakerNameValue };
      const localMeta = { speakerClientId: this.clientId, speakerId: this.currentUserIdValue, speakerName: this.speakerNameValue };
      const sourceLine = this.applyTranscriptUpdate(this.sourceTranscript, data.source_transcript_update, localMeta);
      this.renderSourceTranscript();
      this.broadcastTranscriptUpdate("source", data.source_transcript_update, null, { sourceLineId: sourceLine?.sourceLineId });
      this.renderDebug();
      return;
    }

    if (data.target_transcript_update) {
      this.transcriptMessageCount += 1;
      // LOCAL TESTING ONLY: speakerClientId lets same-user local tabs be treated as separate speakers.
      // PROD VERSION:
      // const localMeta = { speakerId: this.currentUserIdValue, speakerName: this.speakerNameValue };
      const localMeta = { speakerClientId: this.clientId, speakerId: this.currentUserIdValue, speakerName: this.speakerNameValue };
      this.translationTranscript.language = data.target_transcript_update.language || this.languageSelectTarget.value;
      const sourceLine = this.applyTranslationUpdate(data.target_transcript_update, localMeta, this.translationTranscript.language);
      this.renderTranslationTranscript();
      this.broadcastTranscriptUpdate("translation", data.target_transcript_update, data.target_transcript_update.language, { sourceLineId: sourceLine?.sourceLineId });
      this.renderDebug();
      return;
    }

    if (data.error) {
      this.receivedTerminalEvent = true;
      this.resolveFinalization();
      const requestType = data.error.request_type ? ` [${data.error.request_type}]` : "";
      this.lastSocketError = `${data.error.error_message || "DeepL a retourne une erreur."}${requestType}`;
      this.renderDebug();
      this.setError(`${data.error.error_message || "DeepL a retourne une erreur."}${requestType}`);
      this.teardown({ preserveTranscript: true });
      return;
    }

    if (data.end_of_stream) {
      this.receivedTerminalEvent = true;
      this.resolveFinalization();
      this.renderDebug();
      if (!this.intentionalStop && this.transcriptMessageCount === 0) {
        if (this.chunksWithSignalCount > 0) {
          this.setError("DeepL a termine le flux sans produire de transcription, bien qu'un signal micro ait ete detecte.");
        } else {
          this.setError("DeepL a termine le flux sans recevoir d'audio exploitable. Verifiez l'autorisation micro et le microphone utilise.");
        }
        this.teardown({ preserveTranscript: true });
      }
    }
  }

  applyTranscriptUpdate(buffer, update, metadata = {}) {
    const concludedText = this.joinSegments(update.concluded || []);
    const tentativeText = this.joinSegments(update.tentative || []);

    let sourceLine = null;
    if (concludedText) {
      sourceLine = this.pushRecentTranscriptLine(buffer, concludedText, metadata);
    }
    buffer.tentativeText = tentativeText;
    // LOCAL TESTING ONLY: carries the tab identity while DeepL still has tentative text.
    // PROD VERSION: remove/comment this tentativeSpeakerClientId line.
    buffer.tentativeSpeakerClientId = tentativeText ? (metadata.speakerClientId || null) : null;
    buffer.tentativeSpeakerId = tentativeText ? (metadata.speakerId || null) : null;
    buffer.tentativeSpeakerName = tentativeText ? (metadata.speakerName || "") : "";
    return sourceLine;
  }

  applyTranslationUpdate(update, metadata = {}, language = null) {
    const concludedText = this.joinSegments(update.concluded || []);
    const tentativeText = this.joinSegments(update.tentative || []);

    let sourceLine = null;
    if (concludedText) {
      sourceLine = this.attachTranslationToSourceLine(concludedText, metadata, language);
    }

    this.translationTranscript.tentativeText = tentativeText;
    // LOCAL TESTING ONLY: carries the tab identity while DeepL still has tentative translated text.
    // PROD VERSION: remove/comment this tentativeSpeakerClientId line.
    this.translationTranscript.tentativeSpeakerClientId = tentativeText ? (metadata.speakerClientId || null) : null;
    this.translationTranscript.tentativeSpeakerId = tentativeText ? (metadata.speakerId || null) : null;
    this.translationTranscript.tentativeSpeakerName = tentativeText ? (metadata.speakerName || "") : "";
    this.translationTranscript.language = language || this.translationTranscript.language;
    return sourceLine;
  }

  attachTranslationToSourceLine(text, metadata = {}, language = null) {
    const sourceLine = this.findSourceLineForTranslation(metadata, text);

    if (!sourceLine) {
      this.pushRecentTranscriptLine(this.translationTranscript, text, { ...metadata, translationLanguage: language });
      return null;
    }

    sourceLine.translationText = sourceLine.translationText
      ? this.mergeTranscriptLines(sourceLine.translationText, text)
      : text;
    sourceLine.translationLanguage = language || sourceLine.translationLanguage || null;
    sourceLine.translationUpdatedAt = Date.now();
    return sourceLine;
  }

  findSourceLineForTranslation(metadata = {}, translationText = "") {
    const speakerKey = this.transcriptSpeakerKey(metadata);
    const sameSpeakerLines = this.sourceTranscript.recentLines.filter((line) => this.transcriptSpeakerKey(line) === speakerKey);
    if (!sameSpeakerLines.length) {
      return null;
    }

    if (metadata.sourceLineId) {
      const exactLine = sameSpeakerLines.find((line) => String(line.sourceLineId || "") === String(metadata.sourceLineId));
      if (exactLine) {
        return exactLine;
      }
    }

    const incomingLooksLikeContinuation = /^[a-zà-ÿ0-9,;:)\]%]/.test(translationText);
    const incompleteTranslationLine = sameSpeakerLines
      .slice()
      .reverse()
      .find((line) => line.translationText && !this.endsSentence(line.translationText) && incomingLooksLikeContinuation);
    if (incompleteTranslationLine) {
      return incompleteTranslationLine;
    }

    return sameSpeakerLines.find((line) => !line.translationText) || null;
  }

  pushRecentTranscriptLine(buffer, text, metadata = {}) {
    if (!text) {
      return;
    }

    const previousLine = buffer.recentLines[buffer.recentLines.length - 1];
    if (previousLine && this.shouldMergeTranscriptLines(previousLine, text, metadata)) {
      const mergedText = this.mergeTranscriptLines(previousLine.text, text);
      if (mergedText && mergedText !== previousLine.text) {
        previousLine.text = mergedText;
        previousLine.sourceLineId = metadata.sourceLineId || previousLine.sourceLineId || previousLine.id;
        // LOCAL TESTING ONLY: preserves browser-tab identity when merging same-account local lines.
        // PROD VERSION: remove/comment this speakerClientId line.
        previousLine.speakerClientId = metadata.speakerClientId || previousLine.speakerClientId || null;
        previousLine.speakerId = metadata.speakerId || previousLine.speakerId || null;
        previousLine.speakerName = metadata.speakerName || previousLine.speakerName || "";
        previousLine.messageId = metadata.messageId || previousLine.messageId || null;
        previousLine.createdAt = Date.now();
        return previousLine;
      }
    }

    const lineId = this.nextTranscriptLineId++;
    const line = {
      id: lineId,
      sourceLineId: metadata.sourceLineId || (buffer === this.sourceTranscript ? lineId : null),
      text,
      // LOCAL TESTING ONLY: stores browser-tab identity for same-account local testing.
      // PROD VERSION: remove/comment this speakerClientId line.
      speakerClientId: metadata.speakerClientId || null,
      speakerId: metadata.speakerId || null,
      speakerName: metadata.speakerName || "",
      messageId: metadata.messageId || null,
      translationText: metadata.translationText || "",
      translationLanguage: metadata.translationLanguage || null,
      translationUpdatedAt: null,
      createdAt: Date.now()
    };
    buffer.recentLines.push(line);

    if (buffer === this.sourceTranscript) {
      this.attachOrphanTranslationToSourceLine(line);
    }

    return line;
  }

  attachOrphanTranslationToSourceLine(sourceLine) {
    const sourceSpeakerKey = this.transcriptSpeakerKey(sourceLine);
    const orphanIndex = this.translationTranscript.recentLines.findIndex((line) => {
      const sameSourceLine = line.sourceLineId && sourceLine.sourceLineId && String(line.sourceLineId) === String(sourceLine.sourceLineId);
      return sameSourceLine || (!line.sourceLineId && this.transcriptSpeakerKey(line) === sourceSpeakerKey);
    });
    if (orphanIndex === -1) {
      return;
    }

    const [orphanTranslation] = this.translationTranscript.recentLines.splice(orphanIndex, 1);
    sourceLine.translationText = orphanTranslation.text;
    sourceLine.translationLanguage = orphanTranslation.translationLanguage || this.translationTranscript.language || null;
    sourceLine.translationUpdatedAt = Date.now();
  }

  joinSegments(segments) {
    return segments
      .map((segment) => (segment.text || "").trim())
      .filter(Boolean)
      .join(" ")
      .trim();
  }

  concatTranscript(existingText, incomingText) {
    if (!incomingText) {
      return existingText;
    }
    if (!existingText) {
      return incomingText;
    }

    const needsSpace = !/[\s'’-]$/.test(existingText) && !/^[,.;:!?)]/.test(incomingText);
    return needsSpace ? `${existingText} ${incomingText}` : `${existingText}${incomingText}`;
  }

  shouldMergeTranscriptLines(previousLine, incomingText, metadata = {}) {
    const existingText = previousLine?.text || "";
    if (!existingText || !incomingText) {
      return false;
    }

    if (!this.sameTranscriptSpeaker(previousLine, metadata)) {
      return false;
    }

    if (previousLine?.sourceLineId && metadata.sourceLineId && String(previousLine.sourceLineId) !== String(metadata.sourceLineId)) {
      return false;
    }

    if (previousLine?.sourceLineId && metadata.sourceLineId && String(previousLine.sourceLineId) === String(metadata.sourceLineId)) {
      return true;
    }

    if (previousLine?.messageId && metadata.messageId && previousLine.messageId !== metadata.messageId) {
      return false;
    }

    if (previousLine?.messageId && metadata.messageId && previousLine.messageId === metadata.messageId) {
      return true;
    }

    if (incomingText === existingText) {
      return true;
    }

    if (incomingText.startsWith(existingText) || existingText.startsWith(incomingText)) {
      return true;
    }

    if (this.computeTranscriptOverlap(existingText, incomingText) > 0) {
      return true;
    }

    const lineAgeMs = Date.now() - (previousLine.createdAt || 0);
    const incomingStartsLikeContinuation = /^[a-zà-ÿ0-9,;:)\]%]/i.test(incomingText) && !this.endsSentence(existingText);
    const shortRecentLine = lineAgeMs <= CAPTION_LINE_GROUP_WINDOW_MS && existingText.length < CAPTION_LINE_MAX_LENGTH;

    if (incomingStartsLikeContinuation && shortRecentLine) {
      return true;
    }

    if (!this.endsSentence(existingText) && shortRecentLine) {
      return true;
    }

    return false;
  }

  sameTranscriptSpeaker(previousLine, metadata = {}) {
    // LOCAL TESTING ONLY: compare browser-tab ids before user ids so two local tabs on the same account stay separated.
    // PROD VERSION:
    // const existingSpeakerId = previousLine?.speakerId || null;
    // const incomingSpeakerId = metadata.speakerId || null;
    // if (existingSpeakerId || incomingSpeakerId) {
    //   return existingSpeakerId === incomingSpeakerId;
    // }
    const existingSpeakerClientId = previousLine?.speakerClientId || null;
    const incomingSpeakerClientId = metadata.speakerClientId || null;
    if (existingSpeakerClientId || incomingSpeakerClientId) {
      return existingSpeakerClientId === incomingSpeakerClientId;
    }

    const existingSpeakerId = previousLine?.speakerId || null;
    const incomingSpeakerId = metadata.speakerId || null;
    if (existingSpeakerId || incomingSpeakerId) {
      return existingSpeakerId === incomingSpeakerId;
    }

    const existingSpeakerName = (previousLine?.speakerName || "").trim().toLowerCase();
    const incomingSpeakerName = (metadata.speakerName || "").trim().toLowerCase();
    if (existingSpeakerName || incomingSpeakerName) {
      return existingSpeakerName === incomingSpeakerName;
    }

    return true;
  }

  mergeTranscriptLines(existingText, incomingText) {
    if (!existingText) {
      return incomingText;
    }

    if (incomingText.startsWith(existingText)) {
      return incomingText;
    }

    if (existingText.endsWith(incomingText)) {
      return existingText;
    }

    const overlapLength = this.computeTranscriptOverlap(existingText, incomingText);
    if (overlapLength > 0) {
      return `${existingText}${incomingText.slice(overlapLength)}`;
    }

    return this.concatTranscript(existingText, incomingText);
  }

  computeTranscriptOverlap(existingText, incomingText) {
    const maxLength = Math.min(existingText.length, incomingText.length);

    for (let overlapLength = maxLength; overlapLength > 0; overlapLength -= 1) {
      if (existingText.slice(-overlapLength) === incomingText.slice(0, overlapLength)) {
        return overlapLength;
      }
    }

    return 0;
  }

  endsSentence(text) {
    return /[.!?…][)"'\]]?\s*$/.test((text || "").trim());
  }

  renderSourceTranscript() {
    this.renderTranscripts();
  }

  renderTranslationTranscript() {
    if (this.translationEnabled) {
      this.syncTranslationControls();
    }
    this.renderTranscripts();
  }

  renderTranscripts() {
    const sourceLines = this.sourceTranscript.recentLines;
    const orphanTranslationLines = this.translationEnabled
      ? this.translationTranscript.recentLines.filter((line) => !line.sourceLineId)
      : [];

    let html = "";
    sourceLines.forEach((srcLine) => {
      if (!this.translationEnabled || this.transcriptLineIsLocal(srcLine) || !srcLine.translationText) {
        return;
      }

      const match = {
        text: srcLine.translationText,
        // LOCAL TESTING ONLY: keeps rendered remote translations associated with the source browser tab.
        // PROD VERSION: remove/comment this speakerClientId line.
        speakerClientId: srcLine.speakerClientId,
        speakerId: srcLine.speakerId,
        speakerName: srcLine.speakerName,
        language: srcLine.translationLanguage
      };
      html += this.renderCaptionLineHtml(match);
    });

    orphanTranslationLines.forEach((tl) => {
      if (this.transcriptLineIsLocal(tl)) {
        return;
      }

      html += this.renderCaptionLineHtml(tl);
    });

    const hasTentativeTranslation = this.translationEnabled && Boolean(this.translationTranscript.tentativeText);
    if (hasTentativeTranslation) {
      const tentativeTranslation = {
        text: this.translationTranscript.tentativeText,
        // LOCAL TESTING ONLY: keeps tentative translated text associated with the source browser tab.
        // PROD VERSION: remove/comment this speakerClientId line.
        speakerClientId: this.translationTranscript.tentativeSpeakerClientId,
        speakerId: this.translationTranscript.tentativeSpeakerId,
        speakerName: this.translationTranscript.tentativeSpeakerName,
        tentative: true
      };

      if (!this.transcriptLineIsLocal(tentativeTranslation)) {
        html += this.renderCaptionLineHtml(tentativeTranslation);
      }
    }

    this.sourceOutputTarget.innerHTML = html;
    this.sourceEmptyTarget.hidden = html.length > 0;

    if (html.length > 0) {
      this.sourceOutputTarget.scrollTop = this.sourceOutputTarget.scrollHeight;
    }
  }

  renderCaptionLineHtml(line) {
    if (!line?.text) return "";

    const classes = ["live-captions__bubble"];
    if (line.tentative) classes.push("live-captions__bubble--tentative");

    const rawName = line.speakerName || "";
    const speakerName = this.escapeHtml(rawName);
    const speakerHtml = speakerName ? `<span class="live-captions__bubble-speaker">${speakerName}</span>` : "";
    const contentHtml = `<div class="live-captions__bubble-translation">${this.escapeHtml(line.text)}</div>`;

    return `<div class="${classes.join(" ")}">${speakerHtml}${contentHtml}</div>`;
  }

  transcriptLineIsLocal(line) {
    if (!line) {
      return false;
    }

    // LOCAL TESTING ONLY: hide only this browser tab's own captions, even when another local tab has the same user id.
    // PROD VERSION:
    // return Boolean(line.speakerId && Number(line.speakerId) === this.currentUserIdValue);
    if (line.speakerClientId) {
      return line.speakerClientId === this.clientId;
    }

    return Boolean(line.speakerId && Number(line.speakerId) === this.currentUserIdValue);
  }

  transcriptSpeakerKey(line) {
    // LOCAL TESTING ONLY: group transcript lines by browser tab before falling back to user id.
    // PROD VERSION:
    // return `user:${line?.speakerId || ""}`;
    if (line?.speakerClientId) {
      return `client:${line.speakerClientId}`;
    }

    return `user:${line?.speakerId || ""}`;
  }

  renderTranscriptLineHtml(line, opacity = 1) {
    const lineText = this.escapeHtml(line.text || "");
    const speakerName = this.escapeHtml(line.speakerName || "");
    const classes = ["live-captions__line"];

    if (line.tentative) {
      classes.push("live-captions__tentative");
    }

    const speakerHtml = speakerName ? `<span class="live-captions__speaker">${speakerName} :</span> ` : "";
    return `<div class="${classes.join(" ")}" style="opacity:${opacity}">${speakerHtml}${lineText}</div>`;
  }

  captionLineOpacity(index, totalLines) {
    if (totalLines <= 1) {
      return 1;
    }

    const distanceFromNewest = totalLines - index - 1;
    return Math.max(0.45, 1 - (distanceFromNewest * 0.22));
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  arrayBufferToBase64(buffer) {
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = "";

    for (let index = 0; index < bytes.length; index += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
    }

    return window.btoa(binary);
  }

  async decodeSocketPayload(data) {
    if (typeof data === "string") {
      return data;
    }

    if (data instanceof Blob) {
      return data.text();
    }

    if (data instanceof ArrayBuffer) {
      return new TextDecoder().decode(data);
    }

    if (ArrayBuffer.isView(data)) {
      return new TextDecoder().decode(data);
    }

    throw new Error(`Type de message websocket inattendu: ${Object.prototype.toString.call(data)}`);
  }

  async postJson(url, payload) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json"
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(payload)
    });

    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body.error || `Erreur HTTP ${response.status}`);
    }

    return body;
  }

  subscribeToMeetingChannel() {
    if (!this.hasMeetingIdValue || !this.meetingIdValue) {
      return;
    }

    this.meetingChannelSubscription = consumer.subscriptions.create(
      { channel: "LiveCaptionsChannel", meeting_id: this.meetingIdValue, meeting_token: this.meetingTokenValue },
      {
        connected: () => {
          this.meetingChannelConnected = true;
          this.broadcastTranslationPreferences({ responseRequested: true });
          this.renderDebug();
        },
        disconnected: () => {
          this.meetingChannelConnected = false;
          this.renderDebug();
        },
        received: (data) => {
          this.handleMeetingChannelMessage(data);
        }
      }
    );
  }

  unsubscribeFromMeetingChannel() {
    if (!this.meetingChannelSubscription) {
      return;
    }

    consumer.subscriptions.remove(this.meetingChannelSubscription);
    this.meetingChannelSubscription = null;
    this.meetingChannelConnected = false;
  }

  handleMeetingChannelMessage(data) {
    // LOCAL TESTING ONLY: ignore only messages from this browser tab, not every message with the same user id.
    // PROD VERSION:
    // if (!data || Number(data.sender_user_id) === this.currentUserIdValue) {
    //   return;
    // }
    if (!data || data.sender_client_id === this.clientId) {
      return;
    }

    if (data.type === "preferences") {
      this.applyPeerPreferences(data);
      return;
    }

    if (data.type === "transcript_update") {
      this.applyRemoteTranscriptUpdate(data);
    }
  }

  applyPeerPreferences(data) {
    const nextTranslationEnabled = Boolean(data.translation_enabled);
    const nextTargetLanguage = nextTranslationEnabled ? (data.target_language || null) : null;
    const preferencesChanged = this.peerTranslationEnabled !== nextTranslationEnabled || this.peerTargetLanguage !== nextTargetLanguage;

    this.peerTranslationEnabled = nextTranslationEnabled;
    this.peerTargetLanguage = nextTargetLanguage;
    this.renderDebug();

    if (data.response_requested) {
      this.broadcastTranslationPreferences({ responseRequested: false });
    }

    if (preferencesChanged) {
      this.autoPausedForSilence = false;
      this.queueAutomaticStreamingEvaluation();
      this.restartStreamingForPeerPreferences();
    }
  }

  applyRemoteTranscriptUpdate(data) {
    const metadata = {
      // LOCAL TESTING ONLY: preserves sender browser-tab identity for same-account local testing.
      // PROD VERSION: remove/comment this speakerClientId line.
      speakerClientId: data.sender_client_id || null,
      speakerId: data.sender_user_id || null,
      speakerName: data.speaker_name || "Interlocuteur",
      sourceLineId: data.source_line_id || null
    };

    if (data.transcript_kind === "source") {
      this.applyTranscriptUpdate(this.sourceTranscript, data.transcript_update || {}, metadata);
      this.renderSourceTranscript();
      this.renderDebug();
      return;
    }

    if (data.transcript_kind === "translation" && this.translationEnabled) {
      const incomingLanguage = data.language || null;
      if (incomingLanguage && this.normalizeLanguageTag(incomingLanguage) !== this.normalizeLanguageTag(this.languageSelectTarget.value)) {
        return;
      }

      this.translationTranscript.language = incomingLanguage || this.languageSelectTarget.value;
      this.applyTranslationUpdate(data.transcript_update || {}, metadata, this.translationTranscript.language);
      this.renderTranslationTranscript();
      this.renderDebug();
    }
  }

  broadcastTranscriptUpdate(transcriptKind, transcriptUpdate, language = null, options = {}) {
    if (!this.meetingChannelSubscription || !this.meetingChannelConnected) {
      return;
    }

    this.meetingChannelSubscription.perform("relay_transcript", {
      // LOCAL TESTING ONLY: sends browser-tab identity through ActionCable for same-account local testing.
      // PROD VERSION: remove/comment this sender_client_id line.
      sender_client_id: this.clientId,
      sender_user_id: this.currentUserIdValue,
      source_line_id: options.sourceLineId || null,
      transcript_kind: transcriptKind,
      transcript_update: transcriptUpdate,
      language,
      speaker_name: this.speakerNameValue
    });
  }

  broadcastTranslationPreferences({ responseRequested = false } = {}) {
    if (!this.meetingChannelSubscription || !this.meetingChannelConnected) {
      return;
    }

    this.meetingChannelSubscription.perform("update_preferences", {
      // LOCAL TESTING ONLY: sends browser-tab identity through ActionCable for same-account local testing.
      // PROD VERSION: remove/comment this sender_client_id line.
      sender_client_id: this.clientId,
      sender_user_id: this.currentUserIdValue,
      response_requested: responseRequested,
      translation_enabled: this.translationEnabled,
      target_language: this.translationEnabled ? this.languageSelectTarget.value : null
    });
  }

  targetLanguagesChanged() {
    return this.targetLanguagesSignature(this.activeTargetLanguages || []) !== this.targetLanguagesSignature(this.outgoingTargetLanguages);
  }

  targetLanguagesSignature(languages) {
    return [...languages].map((language) => this.normalizeLanguageTag(language)).sort().join(",");
  }

  async restartStreamingForLocalPreferences() {
    if (!this.isRunning || this.isRestartingForLocalPreferences || this.isRestartingForPeerPreferences) {
      return;
    }

    this.isRestartingForLocalPreferences = true;
    this.setStatus("Mise a jour de la langue de traduction...", PENDING_STATUS);
    const restartAutomaticStreaming = this.automaticStreaming;

    try {
      await this.stopStreaming({ preserveTranscript: true, finalStatusMessage: null });
      if (!this.element.isConnected) {
        return;
      }
      await this.startStreaming({ preserveTranscript: true, automatic: restartAutomaticStreaming });
    } finally {
      this.isRestartingForLocalPreferences = false;
    }
  }

  async restartStreamingForPeerPreferences() {
    if (!this.isRunning || this.isRestartingForPeerPreferences || this.isRestartingForLocalPreferences) {
      return;
    }

    this.isRestartingForPeerPreferences = true;
    this.setStatus("Mise a jour de la traduction demandee par l'interlocuteur...", PENDING_STATUS);
    const restartAutomaticStreaming = this.automaticStreaming;

    try {
      await this.stopStreaming({ preserveTranscript: true, finalStatusMessage: null });
      if (!this.element.isConnected) {
        return;
      }
      await this.startStreaming({ preserveTranscript: true, automatic: restartAutomaticStreaming });
    } finally {
      this.isRestartingForPeerPreferences = false;
    }
  }

  resetRemoteTranslationBuffer() {
    // PROD VERSION:
    // this.translationTranscript = { recentLines: [], tentativeText: "", tentativeSpeakerId: null, tentativeSpeakerName: "", language: null };
    // LOCAL TESTING ONLY: this expanded object adds tentativeSpeakerClientId for same-account local testing.
    this.translationTranscript = {
      recentLines: [],
      tentativeText: "",
      // LOCAL TESTING ONLY: clears the temporary browser-tab identity with the remote translation buffer.
      // PROD VERSION: remove/comment this tentativeSpeakerClientId line.
      tentativeSpeakerClientId: null,
      tentativeSpeakerId: null,
      tentativeSpeakerName: "",
      language: null
    };
  }

  resolveFinalization() {
    if (this.finalizationResolver) {
      this.finalizationResolver();
      this.finalizationResolver = null;
    }
  }

  startFirstChunkWatchdog() {
    this.clearFirstChunkWatchdog();
    this.firstChunkTimeout = window.setTimeout(() => {
      if (this.chunksProducedCount === 0 && this.isRunning) {
        this.setError("Le micro est autorise mais aucun chunk audio n'est produit. Essayez Chrome ou Edge et verifiez le micro selectionne.");
      }
    }, FIRST_CHUNK_TIMEOUT_MS);
  }

  clearFirstChunkWatchdog() {
    if (this.firstChunkTimeout) {
      window.clearTimeout(this.firstChunkTimeout);
      this.firstChunkTimeout = null;
    }
  }

  renderDebug() {
    if (!this.hasDebugOutputTarget) {
      return;
    }

    this.debugOutputTarget.textContent = [
      `meeting_id: ${this.meetingIdValue || "n/a"}`,
      `current_user_id: ${this.currentUserIdValue || "n/a"}`,
      // LOCAL TESTING ONLY: shows the browser-tab identity in debug output.
      // PROD VERSION: remove/comment this client_id debug line.
      `client_id: ${this.clientId || "n/a"}`,
      `speaker_name: ${this.speakerNameValue || "n/a"}`,
      `meeting_channel_connected: ${this.meetingChannelConnected}`,
      `peer_translation_enabled: ${this.peerTranslationEnabled}`,
      `peer_target_language: ${this.peerTargetLanguage || "none"}`,
      `weglot_language: ${this.lastWeglotLanguage || this.currentWeglotLanguage() || "none"}`,
      `manual_target_language_selected: ${this.manualTargetLanguageSelected}`,
      `manual_streaming_override: ${this.userToggledStreaming}`,
      `automatic_streaming: ${this.automaticStreaming}`,
      `automatic_translation_needed: ${this.automaticTranslationNeeded()}`,
      `automatic_block_reason: ${this.lastAutomaticBlockReason || "none"}`,
      `automatic_error: ${this.lastAutomaticStreamingError || "none"}`,
      `auto_paused_for_silence: ${this.autoPausedForSilence}`,
      `jitsi_joined: ${this.jitsiState?.conferenceJoined}`,
      `jitsi_participant_count: ${this.jitsiState?.participantCount || 0}`,
      `jitsi_local_audio_muted: ${this.jitsiState?.localAudioMuted}`,
      `jitsi_local_audio_available: ${this.jitsiState?.localAudioAvailable}`,
      `jitsi_remote_audio_muted: ${JSON.stringify(this.jitsiState?.remoteAudioMuted || {})}`,
      `jitsi_state_reason: ${this.jitsiState?.reason || "none"}`,
      `voice_monitor_running: ${this.voiceMonitorRunning}`,
      `voice_monitor_last_rms: ${this.voiceMonitorLastRms.toFixed(4)}`,
      `voice_monitor_max_rms: ${this.voiceMonitorMaxRms.toFixed(4)}`,
      `local_translation_enabled: ${this.translationEnabled}`,
      `local_target_language: ${this.languageSelectTarget.value || "none"}`,
      `outgoing_target_languages: ${this.outgoingTargetLanguages.join(",") || "none"}`,
      `active_target_languages: ${(this.activeTargetLanguages || []).join(",") || "none"}`,
      `source_language: ${this.currentSourceLanguage || "auto"}`,
      `source_language_mode: ${this.currentSourceLanguageMode}`,
      "transport: microphone",
      `source_media_content_type: ${this.sourceMediaContentType || "n/a"}`,
      `selected_microphone_id: ${this.selectedMicrophoneId || "default"}`,
      `selected_microphone_label: ${this.selectedMicrophoneLabel()}`,
      `recorder_mime_type: ${this.recorderMimeType || "n/a"}`,
      `audio_sample_rate: ${this.audioSampleRate || "n/a"}`,
      `target_audio_sample_rate: ${this.targetAudioSampleRate || "n/a"}`,
      `capture_track_label: ${this.captureTrack?.label || "n/a"}`,
      `capture_track_enabled: ${this.captureTrack ? this.captureTrack.enabled : "n/a"}`,
      `capture_track_muted: ${this.captureTrack ? this.captureTrack.muted : "n/a"}`,
      `capture_track_ready_state: ${this.captureTrack?.readyState || "n/a"}`,
      `capture_track_settings: ${this.formatCaptureTrackSettings(this.captureTrackSettings)}`,
      `session_id: ${this.sessionId || "n/a"}`,
      `chunks_produced: ${this.chunksProducedCount}`,
      `chunks_sent: ${this.chunksSentCount}`,
      `chunks_with_signal: ${this.chunksWithSignalCount}`,
      `noise_gate_threshold: ${PCM_NOISE_GATE_THRESHOLD.toFixed(4)}`,
      `silence_chunk_streak: ${this.silenceChunkStreak}`,
      `last_chunk_bytes: ${this.lastChunkByteSize}`,
      `last_raw_chunk_rms: ${this.lastRawChunkRms.toFixed(4)}`,
      `max_raw_chunk_rms: ${this.maxRawChunkRms.toFixed(4)}`,
      `last_chunk_rms: ${this.lastChunkRms.toFixed(4)}`,
      `max_chunk_rms: ${this.maxChunkRms.toFixed(4)}`,
      `transcript_messages: ${this.transcriptMessageCount}`,
      `last_socket_event: ${this.lastSocketEvent}`,
      `last_socket_error: ${this.lastSocketError}`
    ].join("\n");
  }

  floatTo16BitSample(sample) {
    const normalized = Math.max(-1, Math.min(1, sample));
    return normalized < 0 ? Math.round(normalized * 0x8000) : Math.round(normalized * 0x7fff);
  }

  resampleChannelData(channelData, inputRate, outputRate) {
    if (!channelData.length || inputRate === outputRate) {
      return channelData;
    }

    const sampleRatio = inputRate / outputRate;
    const outputLength = Math.max(1, Math.round(channelData.length / sampleRatio));
    const output = new Float32Array(outputLength);

    for (let index = 0; index < outputLength; index += 1) {
      const start = Math.floor(index * sampleRatio);
      const end = Math.min(channelData.length, Math.floor((index + 1) * sampleRatio));
      let sum = 0;
      let count = 0;

      for (let cursor = start; cursor < Math.max(start + 1, end); cursor += 1) {
        sum += channelData[cursor];
        count += 1;
      }

      output[index] = count > 0 ? (sum / count) : channelData[Math.min(start, channelData.length - 1)];
    }

    return output;
  }

  calculateInt16Rms(samples) {
    if (!samples.length) {
      return 0;
    }

    let total = 0;
    for (let index = 0; index < samples.length; index += 1) {
      const normalized = samples[index] / 32768;
      total += normalized * normalized;
    }

    return Math.sqrt(total / samples.length);
  }

  formatCaptureTrackSettings(settings) {
    if (!settings || Object.keys(settings).length === 0) {
      return "n/a";
    }

    const compactSettings = {
      channelCount: settings.channelCount,
      sampleRate: settings.sampleRate,
      sampleSize: settings.sampleSize,
      latency: settings.latency,
      autoGainControl: settings.autoGainControl,
      echoCancellation: settings.echoCancellation,
      noiseSuppression: settings.noiseSuppression,
      voiceIsolation: settings.voiceIsolation
    };

    return JSON.stringify(compactSettings);
  }

  selectedMicrophoneLabel() {
    if (!this.hasMicrophoneSelectTarget) {
      return "n/a";
    }

    return this.microphoneSelectTarget.selectedOptions[0]?.textContent || "Microphone par défaut";
  }

  // LOCAL TESTING ONLY: generates a browser-tab identity for same-account local testing.
  // PROD VERSION: remove/comment this whole buildClientId method.
  buildClientId() {
    const randomValue = window.crypto?.getRandomValues
      ? window.crypto.getRandomValues(new Uint32Array(2)).join("-")
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    return `live-captions-${randomValue}`;
  }

  calculateFloatRms(samples) {
    if (!samples.length) {
      return 0;
    }

    let total = 0;
    for (let index = 0; index < samples.length; index += 1) {
      total += samples[index] * samples[index];
    }

    return Math.sqrt(total / samples.length);
  }

  int16SamplesToBase64(samples) {
    const buffer = new ArrayBuffer(samples.length * 2);
    const view = new DataView(buffer);

    samples.forEach((sample, index) => {
      view.setInt16(index * 2, sample, true);
    });

    return this.arrayBufferToBase64(buffer);
  }
}
